import React from "react";
import Layout from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  GraduationCap, PlayCircle, FileText, ClipboardList, Camera, Sparkles,
  Bot, Banknote, ShieldCheck, MessageCircle,
} from "lucide-react";

/** Curated Easy-Gutachter onboarding & training material. */
const TUTORIALS = [
  {
    id: "intro",
    icon: PlayCircle,
    badge: "Erste Schritte",
    title: "Erste Akte in 5 Minuten anlegen",
    duration: "5:32 Min",
    description: "Vom Login bis zum fertigen PDF: lege deine erste Akte an, lade Bilder hoch, unterschreibe und exportiere.",
    color: "from-emerald-500 to-cyan-500",
  },
  {
    id: "fahrzeugschein",
    icon: Camera,
    badge: "OCR & Auto-Analyse",
    title: "Fahrzeugschein scannen & Daten übernehmen",
    duration: "3:10 Min",
    description: "Mit Live-Kamera oder Foto in Sekundenschnelle Halter, VIN, Kennzeichen und Fahrzeugdaten füllen.",
    color: "from-cyan-500 to-sky-500",
  },
  {
    id: "vorschaden",
    icon: ClipboardList,
    badge: "Vorschaden",
    title: "Bauteile erfassen — Neu, Alt, Vor",
    duration: "4:45 Min",
    description: "Klickbare Karosserie-Übersicht, Multi-Status pro Bauteil und automatische PDF-Übernahme.",
    color: "from-violet-500 to-fuchsia-500",
  },
  {
    id: "fotos",
    icon: Sparkles,
    badge: "Auto-Schadensanalyse",
    title: "Auto-Schadensbeschreibung aus Fotos",
    duration: "2:20 Min",
    description: "Pro Bild: ein Klick — automatisch wird eine fertige Schadensbeschreibung in Gutachterdeutsch erstellt.",
    color: "from-amber-500 to-orange-500",
  },
  {
    id: "rechnungen",
    icon: Banknote,
    badge: "Abrechnung",
    title: "Rechnungen, IBAN & Steuernummer einrichten",
    duration: "2:00 Min",
    description: "So hinterlegst du deine Bankdaten und USt-IdNr. damit Rechnungen korrekt erzeugt werden.",
    color: "from-emerald-500 to-teal-500",
  },
  {
    id: "team",
    icon: ShieldCheck,
    badge: "Team & Backoffice",
    title: "Backoffice einrichten und Akten verteilen",
    duration: "3:25 Min",
    description: "Rollen verstehen, Mitarbeiter einladen, Akten zuweisen und freigeben.",
    color: "from-sky-500 to-blue-600",
  },
];

const RESOURCES = [
  { icon: FileText, title: "PDF-Schnellstart-Anleitung", description: "12 Seiten, druckbar — ideal für Mitarbeiter-Onboarding.", action: "Herunterladen" },
  { icon: Bot, title: "Live-Webinar (jeden Donnerstag)", description: "60 Min mit Live-Q&A. Kostenlos für alle Abonnenten.", action: "Anmelden" },
  { icon: MessageCircle, title: "Community-Forum", description: "Kollegen-Austausch, Tipps & Tricks von erfahrenen Gutachtern.", action: "Beitreten" },
];

export default function Schulung() {
  return (
    <Layout title="Schulung" subtitle="Lernvideos, Schritt-für-Schritt-Anleitungen und Live-Trainings">
      <div className="max-w-6xl space-y-10">
        {/* Hero */}
        <div
          className="relative overflow-hidden rounded-2xl border border-zinc-200 bg-gradient-to-br from-emerald-500 to-cyan-500 text-white p-8"
          data-testid="schulung-hero"
        >
          <div className="absolute right-6 top-6 opacity-30">
            <GraduationCap className="h-24 w-24" />
          </div>
          <Badge className="bg-white/20 backdrop-blur border-white/30 text-white mb-3">
            <Sparkles className="h-3 w-3 mr-1" /> Easy Gutachter Akademie
          </Badge>
          <h2 className="font-display text-3xl font-bold tracking-tight max-w-2xl">
            Vom Anfänger zum Profi-Gutachter — in unter 30 Minuten.
          </h2>
          <p className="mt-2 text-white/90 max-w-xl">
            Alle Funktionen, alle Workflows, alle Tricks — als Kurzvideos, druckbare PDFs und Live-Sessions.
          </p>
        </div>

        {/* Tutorials grid */}
        <section>
          <h3 className="font-display text-xl font-bold tracking-tight mb-4">Video-Tutorials</h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {TUTORIALS.map((t) => (
              <Card
                key={t.id}
                className="group overflow-hidden cursor-pointer hover:-translate-y-0.5 hover:shadow-lg transition-all duration-300"
                data-testid={`tutorial-${t.id}`}
              >
                <div className={`relative aspect-video bg-gradient-to-br ${t.color} flex items-center justify-center text-white`}>
                  <t.icon className="h-16 w-16 opacity-60 group-hover:scale-110 transition-transform" strokeWidth={1.4} />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
                  <div className="absolute top-3 left-3">
                    <Badge className="bg-white/95 text-zinc-700 hover:bg-white">{t.badge}</Badge>
                  </div>
                  <div className="absolute bottom-3 right-3 inline-flex items-center gap-1 text-[11px] font-bold bg-black/40 backdrop-blur rounded-md px-2 py-1">
                    <PlayCircle className="h-3 w-3" /> {t.duration}
                  </div>
                </div>
                <div className="p-4">
                  <div className="font-display font-semibold tracking-tight">{t.title}</div>
                  <p className="text-sm text-zinc-500 mt-1">{t.description}</p>
                </div>
              </Card>
            ))}
          </div>
        </section>

        {/* Resources */}
        <section>
          <h3 className="font-display text-xl font-bold tracking-tight mb-4">Weitere Ressourcen</h3>
          <div className="grid sm:grid-cols-3 gap-4">
            {RESOURCES.map((r, i) => (
              <Card key={i} className="p-5 hover:border-emerald-300 transition-colors" data-testid={`resource-${i}`}>
                <div className="h-10 w-10 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center mb-3">
                  <r.icon className="h-5 w-5" strokeWidth={1.8} />
                </div>
                <div className="font-display font-semibold tracking-tight">{r.title}</div>
                <p className="text-sm text-zinc-500 mt-1 mb-3">{r.description}</p>
                <button
                  type="button"
                  className="text-sm font-semibold text-emerald-700 hover:underline"
                  onClick={() => window.alert("Bald verfügbar — wir benachrichtigen dich, sobald die Inhalte online sind.")}
                >
                  {r.action} →
                </button>
              </Card>
            ))}
          </div>
        </section>
      </div>
    </Layout>
  );
}
